<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      緊急連絡先の編集
    </Header>
    <div class="my-4 mx-4">
      <CustomValidationObserver
        v-slot="{ handleSubmit }"
        ref="form"
        tag="div"
        :initialValues="formValues"
      >
        <ValidationField
          v-slot="validationState"
          label="緊急連絡先(電話番号)"
          :rules="{ required: true, numeric: true, min: 10, max: 11 }"
          name="phoneNumber"
          v-model="phoneNumber"
        >
          <PhoneNumberInputGroup
            v-model="phoneNumber"
            label="緊急連絡先(電話番号)"
            placeholder="緊急連絡先(電話番号)"
            class="mb-3"
            :validation-state="validationState"
            data-testid="phoneNumber"
          />
        </ValidationField>
        <ValidationProvider
          v-slot="validationState"
          name="続柄"
          rules="required"
          vid="relationshipToWorker"
        >
          <FormSelectGroup
            v-model="relationshipToWorker"
            :options="relationshipToWorkerOptions"
            label="続柄"
            firstOption="続柄を選択"
            class="mb-3"
            :validation-state="validationState"
            data-testid="relationshipToWorker"
          />
        </ValidationProvider>
        <p class="mb-4 text-mild">
          お仕事中に重大な事故などがあった際に、緊急連絡先に連絡されることがあります。
        </p>
        <AppButton
          block
          @click="handleSubmit(onSubmit)"
          data-testid="submitButton"
        >
          設定する
        </AppButton>
      </CustomValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import { Component, Vue } from "vue-facing-decorator";
import AppButton from "@/components/atoms/button/AppButton.vue";
import PhoneNumberInputGroup from "@/components/molecules/form/PhoneNumberInputGroup.vue";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { accountService } from "@/app/services/AccountService";
import { CustomFormOption } from "@/types/form/FormOption";
import FormSelectGroup from "@/components/molecules/form/FormSelectGroup.vue";

@Component({
  components: {
    FeatherIcon,
    FormSelectGroup,
    PhoneNumberInputGroup,
    Header,
    AppButton
  }
})
export default class EditEmergencyPhoneNumberOfRelationship extends Vue {
  isAlreadyRegistered = true;
  phoneNumber = "";
  relationshipToWorkerOptions: CustomFormOption<number>[] = [];
  relationshipToWorker: number | null = null;

  get formValues() {
    return {
      phoneNumber: this.phoneNumber,
      relationshipToWorker: this.relationshipToWorker
    };
  }

  async created() {
    try {
      this.$loading.show();
      await Promise.all([
        this.fetchRelationshiopToWorker(),
        this.fetchEmergencyContactOfRelationship()
      ]);
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchEmergencyContactOfRelationship() {
    try {
      const {
        phoneNumber,
        relationshipToWorker
      } = await accountService.fetchEmergencyContactOfRelationship();
      this.phoneNumber = phoneNumber;
      this.relationshipToWorker = relationshipToWorker;
      this.isAlreadyRegistered = true;
    } catch {
      this.isAlreadyRegistered = false;
    }
  }

  async fetchRelationshiopToWorker() {
    const { results } = await accountService.fetchRelationshiopToWorker();
    this.relationshipToWorkerOptions = results
      .sort((a, b) => a.sortNo - b.sortNo)
      .map(v => ({
        text: v.name,
        value: v.id
      }));
  }

  async onSubmit() {
    try {
      if (!this.relationshipToWorker) {
        await this.$bvModal.msgBoxOk("続柄を選択してください。", {
          centered: true
        });
        return;
      }
      this.$loading.show();
      if (this.isAlreadyRegistered) {
        await accountService.updateEmergencyContactOfRelationship(
          this.phoneNumber,
          this.relationshipToWorker
        );
      } else {
        await accountService.createEmergencyContactOfRelationship(
          this.phoneNumber,
          this.relationshipToWorker
        );
      }
      this.$router.push("/mypage");
    } catch {
      await this.$bvModal.msgBoxOk("エラーが発生しました", {
        centered: true
      });
    } finally {
      this.$loading.hide();
    }
  }
}
</script>

<style lang="scss" scoped></style>
